import { Skeleton } from "@/components/ui/skeleton";
// import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import GridContainer from "@/features/stage/components/add-edit-stage/confComponents/GridContainer";

interface AdvancedSkeletonProps {
  variant?:
    | "vertical"
    | "horizontal"
    | "sidebar"
    | "table"
    | "card"
    | "avatar"
    | "grid"
    | "board"
    | "full";
  count?: number;
  rows?: number;
  className?: string;
}

export function AdvancedSkeleton({
  variant = "vertical",
  count = 4,
  rows = 5,
  className,
}: AdvancedSkeletonProps) {
  // 🔥 Variant Rendering Logic
  switch (variant) {
    // ---------------------------------------
    case "vertical":
      return (
        <div className="flex flex-col gap-4 w-full py-2">
          {[...Array(rows)].map((_, i) => (
            <div key={i} className="flex items-start gap-4 p-4">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex flex-col gap-2 w-full">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      );

    // ---------------------------------------
    case "horizontal":
      return (
        <div
          className={cn("flex gap-4 overflow-x-hidden px-2 w-full", className)}
        >
          {[...Array(count)].map((_, i) => (
            <div
              key={i}
              className="flex flex-col gap-3 p-4 w-40 min-w-40 bg-white rounded-lg shadow-sm"
            >
              <Skeleton className="h-24 w-full rounded-md" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ))}
        </div>
      );

    // ---------------------------------------
    case "sidebar":
      return (
        <div className={cn("flex flex-col gap-4 w-full px-4 py-2", className)}>
          {[...Array(count)].map((_, i) => (
            <Skeleton key={i} className="h-5 w-full" />
          ))}
        </div>
      );

    // ---------------------------------------
    case "table":
      return (
        <div className={cn("w-full", className)}>
          {[...Array(rows)].map((_, i) => (
            <div key={i} className="flex gap-4 py-3 border-b">
              <Skeleton className="h-4 w-1/6" />
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-1/5" />
            </div>
          ))}
        </div>
      );

    // ---------------------------------------
    case "card":
      return (
        <div
          className={cn("bg-white p-6 rounded-xl shadow-sm w-full", className)}
        >
          <Skeleton className="h-5 w-1/3 mb-4" />
          <Skeleton className="h-3 w-1/4" />
          <Skeleton className="h-44 w-full mt-4 rounded-md" />
        </div>
      );

    // ---------------------------------------
    case "avatar":
      return (
        <div className={cn("flex flex-col gap-4", className)}>
          {[...Array(count)].map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex flex-col gap-2 w-full">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/4" />
              </div>
            </div>
          ))}
        </div>
      );

    // ---------------------------------------
    case "grid":
      return (
        <GridContainer columns={4}>
          {[...Array(count)].map((_, i) => (
            <div
              key={i}
              className="p-4 rounded-xl border border-[#E6E8E7] flex flex-col gap-4 shadow-sm overflow-hidden"
              style={{
                background: "var(--color-card-gradient)",
              }}
            >
              {/* Header */}
              <div className="flex justify-between items-start gap-3">
                <div className="flex gap-3 items-center">
                  <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                  <div className="flex flex-col gap-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-3 w-12" />
                </div>
              </div>

              {/* Body */}
              <div className="flex flex-col gap-3">
                <div className="flex gap-4">
                  <div className="flex items-center gap-2 flex-1">
                    <Skeleton className="h-4 w-4 shrink-0" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                  <div className="flex items-center gap-2 flex-1">
                    <Skeleton className="h-4 w-4 shrink-0" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2 flex-1">
                    <Skeleton className="h-4 w-4 shrink-0" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                  <div className="flex items-center gap-2 flex-1">
                    <Skeleton className="h-4 w-4 shrink-0" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 shrink-0" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              </div>

              <div className="h-px w-full bg-[#E6E8E7] mt-1" />

              {/* Footer */}
              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-16 rounded-full" />
                  <Skeleton className="h-6 w-16 rounded-full" />
                </div>
                <div className="flex gap-2 items-center">
                  <Skeleton className="h-5 w-8" />
                  <Skeleton className="h-6 w-6 rounded-full" />
                </div>
              </div>
            </div>
          ))}
        </GridContainer>
      );

    // ---------------------------------------
    // Kanban board: a horizontal row of status columns, each mirroring the
    // LeadBoard column (color dot + title + count chip, dashed divider, then
    // stacked lead-card skeletons). `count` = number of columns.
    case "board":
      return (
        <div className={cn("flex gap-6 p-4 h-full w-max min-w-full", className)}>
          {[...Array(count)].map((_, i) => (
            <div
              key={i}
              className="min-w-[380px] max-w-[380px] flex-shrink-0 flex flex-col h-full"
            >
              {/* Column header */}
              <div className="flex items-center justify-between p-2 pt-1 pb-3">
                <div className="flex items-center gap-2">
                  <Skeleton className="w-2.5 h-2.5 rounded-full shrink-0" />
                  <Skeleton className="h-5 w-28" />
                  <Skeleton className="h-5 w-8 rounded-full" />
                </div>
                <Skeleton className="h-5 w-5 rounded-md" />
              </div>
              <div className="w-full border-t-2 border-dashed border-[#E6E8E7] mb-4" />

              {/* Cards */}
              <div className="flex flex-col gap-4">
                {[...Array(4)].map((_, j) => (
                  <div
                    key={j}
                    className="p-4 rounded-xl border border-[#E6E8E7] flex flex-col gap-4 shadow-sm overflow-hidden"
                    style={{ background: "var(--color-card-gradient)" }}
                  >
                    {/* Header */}
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex gap-3 items-center">
                        <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                        <div className="flex flex-col gap-2">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-3 w-16" />
                        </div>
                      </div>
                      <Skeleton className="h-3 w-12 shrink-0" />
                    </div>

                    {/* Body */}
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4 shrink-0" />
                        <Skeleton className="h-3 w-full" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4 shrink-0" />
                        <Skeleton className="h-3 w-3/4" />
                      </div>
                    </div>

                    <div className="h-px w-full bg-[#E6E8E7]" />

                    {/* Footer */}
                    <div className="flex justify-between items-center">
                      <div className="flex gap-2">
                        <Skeleton className="h-6 w-16 rounded-full" />
                        <Skeleton className="h-6 w-16 rounded-full" />
                      </div>
                      <Skeleton className="h-6 w-6 rounded-full" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );

    // ---------------------------------------
    case "full":
      return (
        <div
          className={cn(
            "flex flex-col gap-6 w-full h-full px-6 py-8",
            className
          )}
        >
          <Skeleton className="h-8 w-1/3" />

          <div className="flex gap-4">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-20" />
          </div>

          <Skeleton className="h-96 w-full rounded-lg" />
        </div>
      );

    // ---------------------------------------
    default:
      return null;
  }
}
